import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowLeft } from "@phosphor-icons/react";
import MultiToolMark from "@/components/MultiToolMark";

const INCLUDED = [
  "Complete access to all 17+ academic & operational tools — Strategy, Operations and People pillars.",
  "The Tzu Socratic Red-Team engine and automated Tzu Concierge problem-router.",
  "Custom profile white-labeling & corporate branding on shared links and print PDFs.",
  "Google SSO single-gate entry with encrypted sessions.",
  "Strict zero-LLM data-retention headers on every AI request.",
  "Master profile data-purge kill-switch — total data sovereignty on demand.",
];

export default function Pricing() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);

  return (
    <div className="min-h-screen bg-white font-['IBM_Plex_Sans',sans-serif] text-slate-900">
      <header className="h-20 flex items-center justify-between border-b border-slate-200 px-6 lg:px-10 max-w-6xl mx-auto">
        <button data-testid="pricing-brand-home" onClick={() => navigate("/")} className="flex items-center gap-2.5">
          <MultiToolMark size={30} onDark={false} />
          <span className="font-mono text-lg font-bold tracking-tight">MODUS</span>
        </button>
        <button data-testid="pricing-back" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-20 lg:py-24 flex flex-col items-center text-center">
        <p className="font-mono text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">Pricing — Preview</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">Pay as you go. Everything unlocked.</h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
          MODUS is <span className="font-medium text-slate-900">pay-as-you-go — not a subscription</span>. No contracts, no seats, no feature gates and no lock-in. Every framework, the Socratic advisor and every security layer is unlocked from day one.
        </p>
        <span data-testid="payg-badge" className="mt-5 inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 px-3 py-1.5 font-mono text-[11px] font-semibold tracking-[0.12em] uppercase">
          One-time payments · Zero auto-renewals · Forever read-only access
        </span>

        {/* Frequency switch */}
        <div className="mt-10 inline-flex items-center rounded-none border border-slate-300 p-1 bg-slate-50" data-testid="billing-switch">
          <button
            data-testid="bill-monthly"
            onClick={() => setAnnual(false)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors ${!annual ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
          >
            30-Day Project Pass
          </button>
          <button
            data-testid="bill-annually"
            onClick={() => setAnnual(true)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors ${annual ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
          >
            365-Day Pass <span className={annual ? "text-emerald-300" : "text-emerald-600"}>(Best Value)</span>
          </button>
        </div>

        {/* Professional Pass card */}
        <div className="mt-10 w-full max-w-xl border border-slate-900 bg-white text-left" data-testid="professional-pass">
          <div className="p-8 lg:p-10 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-medium tracking-tight">Professional Pass</h2>
              <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase bg-slate-900 text-white px-2 py-1">All-Inclusive</span>
            </div>

            <div className="mt-6 flex items-end gap-2" data-testid="price-display">
              <span className="text-5xl font-light tracking-tight tabular-nums">{annual ? "$599" : "$69"}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500" data-testid="price-subline">
              {annual
                ? "Single payment for 365 days of active access. Includes 50 Tzu Executive Reviews. No auto-renew."
                : "Single payment for 30 days of active access. Includes 5 Tzu Executive Reviews. No auto-renew."}
            </p>

            <button
              data-testid="activate-pass"
              type="button"
              className="mt-7 w-full py-3.5 px-4 bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              {annual ? "Purchase 365-Day Pass" : "Purchase 30-Day Pass"}
            </button>
          </div>

          {/* All-inclusive value matrix */}
          <div className="p-8 lg:p-10">
            <p className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500 mb-5">Everything, unlocked</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="value-matrix">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700 leading-snug">
                  <Check size={16} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-10 font-mono text-xs text-slate-400 tracking-wide">Preview only — checkout is inactive pending billing setup.</p>
      </main>
    </div>
  );
}
